import axios from 'axios'
import { generateSign } from '../utils/sign'

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL ? process.env.VUE_APP_BASE_URL : '/api',
  timeout: 5000
})

const exceptPaths = [
  // '/auth/login',
  '/auth/public-key',
  '/auth/set-admin-password'
]

function shouldSign(url) {
  for (const path of exceptPaths) {
    if (url.includes(path)) {
      return false
    }
  }
  return true
}

service.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }

    const isGet = config.method === 'get'
    const timestamp = Date.now()
    const extra = { timestamp }

    if (shouldSign(config.url)) {
      const source = isGet ? config.params : config.data
      // FormData 无法展开，签名基于其条目迭代值；若签名失败则降级为仅 timestamp
      if (source instanceof FormData) {
        try {
          const obj = {}
          source.forEach((v, k) => { obj[k] = v instanceof File ? v.name : v })
          extra.sign = generateSign({ ...obj, timestamp })
        } catch (e) {
          extra.sign = generateSign({ timestamp })
        }
      } else {
        extra.sign = generateSign({ ...(source || {}), timestamp })
      }
    }

    if (isGet) {
      config.params = { ...config.params, ...extra }
    } else if (config.data instanceof FormData) {
      // FormData 不能被展开覆盖，必须追加
      Object.entries(extra).forEach(([k, v]) => config.data.append(k, v))
    } else {
      config.data = { ...config.data, ...extra }
    }

    return config
  },
  error => Promise.reject(error)
)

service.interceptors.response.use(
  response => {
    const res = response.data
    if (res.code !== 0) {
      if (res.code === 401) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('permissions')
        window.location.href = '/login'
      }
      if (res.code === 4001) {
        console.error('签名校验失败:', res.message)
      }
      const err = new Error(res.message || 'Error')
      err.code = res.code
      err.data = res.data
      return Promise.reject(err)
    }
    return res
  },
  error => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('permissions')
      window.location.href = '/login'
    }
    if (error.response && error.response.data && error.response.data.code === 4001) {
      console.error('签名校验失败:', error.response.data.message)
    }
    // 统一错误格式，确保 error.message 始终可用
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message || '网络错误'
    const err = new Error(message)
    err.code = error.response && error.response.data && error.response.data.code
    err.response = error.response
    return Promise.reject(err)
  }
)

export const authApi = {
  login: data => service.post('/auth/login', data),
  logout: () => service.post('/auth/logout'),
  me: () => service.get('/auth/me')
}

export const userApi = {
  list: params => service.get('/user/index', { params }),
  show: id => service.get(`/user/show/${id}`),
  create: data => service.post('/user/store', data),
  update: (id, data) => service.put(`/user/update/${id}`, data),
  delete: id => service.delete(`/user/destroy/${id}`),
  availableParents: id => service.get(`/user/available-parents${id ? '/' + id : ''}`),
  assignableRoles: () => service.get('/user/assignable-roles')
}

export const roleApi = {
  list: params => service.get('/role/index', { params }),
  show: id => service.get(`/role/show/${id}`),
  create: data => service.post('/role/store', data),
  update: (id, data) => service.put(`/role/update/${id}`, data),
  delete: id => service.delete(`/role/destroy/${id}`),
  all: () => service.get('/role/all'),
  tree: () => service.get('/role/tree'),
  availableParents: id => service.get(`/role/available-parents${id ? '/' + id : ''}`),
  assignableMenus: () => service.get('/role/assignable-menus'),
  permissionTree: id => service.get(`/role/permission-tree/${id}`)
}

export const menuApi = {
  list: params => service.get('/menu/index', { params }),
  show: id => service.get(`/menu/show/${id}`),
  create: data => service.post('/menu/store', data),
  update: (id, data) => service.put(`/menu/update/${id}`, data),
  delete: id => service.delete(`/menu/destroy/${id}`),
  permissions: () => service.get('/menu/permissions'),
  assignableTree: () => service.get('/menu/assignable-tree')
}

export const orderApi = {
  list: params => service.get('/order/list', { params }),
  show: id => service.get(`/order/${id}`)
}

// 题库模块 API
export const questionBankApi = {
  list: params => service.get('/question-bank/list', { params }),
  show: id => service.get(`/question-bank/show/${id}`),
  create: data => service.post('/question-bank/store', data),
  update: (id, data) => service.put(`/question-bank/update/${id}`, data),
  delete: id => service.delete(`/question-bank/destroy/${id}`),
  import: data => service.post('/question-bank/import', data)
}

export const quizConfigApi = {
  list: params => service.get('/question-bank/config/list', { params }),
  all: () => service.get('/question-bank/config/all'),
  show: id => service.get(`/question-bank/config/show/${id}`),
  create: data => service.post('/question-bank/config/store', data),
  update: (id, data) => service.put(`/question-bank/config/update/${id}`, data),
  delete: id => service.delete(`/question-bank/config/destroy/${id}`)
}

export const quizRecordApi = {
  list: params => service.get('/question-bank/record/list', { params }),
  show: id => service.get(`/question-bank/record/show/${id}`),
  delete: id => service.delete(`/question-bank/record/destroy/${id}`),
  start: data => service.post('/question-bank/start', data),
  submit: data => service.post('/question-bank/submit', data),
  stats: () => service.get('/question-bank/stats')
}

export const questionTypeApi = {
  list: params => service.get('/question-type/list', { params }),
  all: () => service.get('/question-type/all'),
  show: id => service.get(`/question-type/show/${id}`),
  create: data => service.post('/question-type/store', data),
  update: (id, data) => service.put(`/question-type/update/${id}`, data),
  delete: id => service.delete(`/question-type/destroy/${id}`),
  getQuestions: (typeId, params) => service.get(`/question-type/${typeId}/questions`, { params }),
  getAvailableQuestions: (typeId, params) => service.get(`/question-type/${typeId}/available-questions`, { params }),
  bindQuestions: (typeId, data) => service.post(`/question-type/${typeId}/bind`, data),
  unbindQuestions: (typeId, data) => service.post(`/question-type/${typeId}/unbind`, data)
}

export const userPurchaseApi = {
  list: params => service.get('/user-purchase/list', { params }),
  show: id => service.get(`/user-purchase/show/${id}`),
  update: (id, data) => service.put(`/user-purchase/update/${id}`, data),
  delete: id => service.delete(`/user-purchase/destroy/${id}`)
}

export const operationLogApi = {
  list: params => service.get('/operation-log/list', { params }),
  detail: id => service.get(`/operation-log/detail/${id}`),
  types: () => service.get('/operation-log/types')
}

export const systemConfigApi = {
  list: params => service.get('/system-config/list', { params }),
  show: id => service.get(`/system-config/show/${id}`),
  create: data => service.post('/system-config/store', data),
  update: (id, data) => service.put(`/system-config/update/${id}`, data),
  delete: id => service.delete(`/system-config/destroy/${id}`)
}

export const carouselApi = {
  list: params => service.get('/carousel/list', { params }),
  show: id => service.get(`/carousel/show/${id}`),
  create: data => service.post('/carousel/store', data),
  update: (id, data) => service.put(`/carousel/update/${id}`, data),
  delete: id => service.delete(`/carousel/destroy/${id}`)
}

export const bannerApi = {
  list: params => service.get('/banner/list', { params }),
  show: id => service.get(`/banner/show/${id}`),
  create: data => service.post('/banner/store', data),
  update: (id, data) => service.put(`/banner/update/${id}`, data),
  delete: id => service.delete(`/banner/destroy/${id}`),
  upload: data => service.post('/attachment/file/upload', data)
}

export default service
